'use client';
import { filterAnnualVisibleRegions } from '@hyphengroup/cms-components/utils/travelInsurance';
import { useMemo } from 'react';
import {
  CmsCountry,
  CmsRegion,
  ComponentTravelPolicySearch,
  TravelInsuranceType,
} from '../../../../../app/[site]/[locale]/(cms)/_modules/cmsService/types';
import { TravelCompareQuotesParams } from '../../../../../app/[site]/[locale]/(cms)/_modules/travelQuotesService/types';
import { GlobalStickyPanel } from '../GlobalStickyPanel';
import { useStickyTopSlotSuspended } from '../StickyTopSlot';
import { CompactTIOptions } from './CompactTIOptions';
import { DesktopTIOptions } from './DesktopTIOptions';
import { useTIOptions } from './useTIOptions';

type TIOptionsProps = {
  searchParams: TravelCompareQuotesParams;
  onChangeParams: (value: TravelCompareQuotesParams) => void;
  countries: CmsCountry[];
  regions: CmsRegion[];
  boundaries: ComponentTravelPolicySearch;
  tripTypes: TravelInsuranceType[];
  queryParamsReady: boolean;
  /** When true, the trip-details panel pins to the top of the viewport while results scroll. */
  sticky?: boolean;
};

export const TIOptions = (props: TIOptionsProps) => {
  const {
    searchParams,
    onChangeParams,
    tripTypes,
    countries,
    regions,
    boundaries,
    queryParamsReady,
    sticky = false,
  } = props;

  const visibleRegions = useMemo(
    () => filterAnnualVisibleRegions(regions, boundaries.annualVisibleRegions),
    [regions, boundaries.annualVisibleRegions],
  );

  // Only the selector options are filtered. useTIOptions gets the full list on
  // purpose: it builds the label map, and a region that is no longer visible can
  // still arrive via URL params - it must render as a label, not a raw code.
  const values = useTIOptions(
    searchParams,
    countries,
    regions,
    boundaries,
    tripTypes,
  );

  const commonProps = {
    searchParams,
    onChangeParams,
    countries,
    regions: visibleRegions,
    boundaries,
    values,
    queryParamsReady,
  };

  // When the TI provider-page nav claims the top slot, M2 un-pins so only one
  // element is sticky at the top (CMS-1643 AC-13). No-op on pages without the nav.
  const suspended = useStickyTopSlotSuspended();

  return (
    <GlobalStickyPanel
      enabled={sticky}
      suspended={suspended}
      className={sticky ? 'py-spacing/gap/y-sm' : undefined}
      // Background only once pinned — transparent over page content while at rest.
      pinnedClassName="bg-fill/alternative-1"
      // Test-hook contract (CMS-1878): trip-type / destination controls. On the
      // panel root rather than a wrapper of its own — the panel is measured and
      // portalled, so an extra node inside it is one more thing to go wrong.
      data-component="ti-options"
    >
      <DesktopTIOptions {...commonProps} />
      <CompactTIOptions {...commonProps} />
    </GlobalStickyPanel>
  );
};
